<template>
  <Teleport to="body">
    <div
      v-show="modelValue"
      class="rms-item-detail-modal"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="item ? 'rms-item-detail-title' : undefined"
      :aria-label="$t('public.itemDetailDialogAria')"
      :aria-hidden="!modelValue"
      @keydown.escape="close"
    >
      <div class="rms-item-detail-modal__backdrop" aria-hidden="true" @click="close" />
      <div
        class="rms-item-detail-modal__panel"
        ref="panelRef"
        @keydown="onPanelKeydown"
      >
        <button
          ref="closeBtnRef"
          type="button"
          class="rms-item-detail-modal__close"
          :style="primaryButtonStyle"
          :aria-label="$t('public.quickOrder.ariaClose')"
          @click="close"
        >
          <span class="material-icons" aria-hidden="true">close</span>
        </button>

        <div
          class="rms-item-detail-modal__scroll"
          :class="{ 'rms-item-detail-modal__scroll--body-empty': !hasBodyContent }"
          v-if="item"
        >
          <!-- Card: image on top (or compact header when no image), gradient overlay, category + name -->
          <div
            class="rms-item-detail-modal__card"
            :class="{ 'rms-item-detail-modal__card--no-image': !cardImageUrl }"
          >
            <div
              class="rms-item-detail-modal__card-bg"
              :class="{ 'rms-item-detail-modal__card-bg--has-image': cardImageUrl }"
            >
              <img
                v-if="cardImageUrl && !cardImageError"
                :src="cardImageUrl"
                :alt="''"
                class="rms-item-detail-modal__card-img"
                aria-hidden="true"
                @error="cardImageError = true"
              />
              <span v-else class="rms-item-detail-modal__card-placeholder" aria-hidden="true">
                <span class="material-symbols-outlined">{{ cardPlaceholderIcon }}</span>
              </span>
            </div>
            <div class="rms-item-detail-modal__card-gradient" aria-hidden="true" />
            <div class="rms-item-detail-modal__card-text">
              <p v-if="categoryName" class="rms-item-detail-modal__card-category ">{{ categoryName }}</p>
              <h2 id="rms-item-detail-title" class="rms-item-detail-modal__card-name ">{{ item.name || $t('public.itemUntitled') }}</h2>
            </div>
          </div>

          <div v-if="hasBodyContent" class="rms-item-detail-modal__body mb-5">
          <!-- Price: simple/combo = single price; with_variants = "From $X.XX" + optional variant list -->
          <div class="rms-item-detail-modal__price-wrap">
            <template v-if="item.type === 'with_variants'">
              <span v-if="effectiveSelectedSku && linePriceWithModifiers != null" class="rms-item-detail-modal__price" :style="primaryTextStyle">
                {{ formatPrice(linePriceWithModifiers) }}
              </span>
              <span v-else-if="lowestVariantPrice != null && !requireVariantSelection" class="rms-item-detail-modal__price" :style="primaryTextStyle">
                {{ $t('public.priceFrom', { price: formatPrice(lowestVariantPrice) }) }}
              </span>
              <span v-else-if="requireVariantSelection && variantSkus(item).length" class="rms-item-detail-modal__price rms-item-detail-modal__muted text-sm">
                {{ $t('public.selectVariantToAdd') }}
              </span>
              <!-- When opened from a variant click, show only that variant (no list). When opened from parent or requireVariantSelection, show the list. -->
              <p v-if="effectiveSelectedSku && !requireVariantSelection" class="rms-item-detail-modal__variant-single font-bold text-lg mt-1">
                {{ variantSkuLabel(effectiveSelectedSku) }}
              </p>
              <!-- Variant list: show when no variant pre-selected (opened from parent) or when requireVariantSelection (must pick in modal) -->
              <ul v-else-if="variantSkus(item).length && (requireVariantSelection || !effectiveSelectedSku)" class="rms-item-detail-modal__variant-list" :aria-label="$t('public.variantOptionsAria')">
                <li
                  v-for="sku in variantSkus(item)"
                  :key="sku.uuid"
                  class="rms-item-detail-modal__variant-row"
                  :class="{
                    'rms-item-detail-modal__variant-row--clickable': requireVariantSelection && isVariantSkuPubliclyOrderable(sku),
                    'rms-item-detail-modal__variant-row--selected': requireVariantSelection && effectiveSelectedSku?.uuid === sku.uuid,
                    'rms-item-detail-modal__variant-row--unavailable': !isVariantSkuPubliclyOrderable(sku),
                  }"
                  :role="requireVariantSelection && isVariantSkuPubliclyOrderable(sku) ? 'button' : undefined"
                  :tabindex="requireVariantSelection && isVariantSkuPubliclyOrderable(sku) ? 0 : undefined"
                  :aria-pressed="requireVariantSelection ? effectiveSelectedSku?.uuid === sku.uuid : undefined"
                  @click="requireVariantSelection && isVariantSkuPubliclyOrderable(sku) ? selectVariant(sku) : undefined"
                  @keydown.enter.space.prevent="requireVariantSelection && isVariantSkuPubliclyOrderable(sku) ? selectVariant(sku) : undefined"
                >
                  <img
                    v-if="resolveImageUrl(sku.image_url ?? sku.imageUrl)"
                    :src="resolveImageUrl(sku.image_url ?? sku.imageUrl)"
                    :alt="variantSkuLabel(sku) || $t('public.variantImageAlt')"
                    loading="lazy"
                    class="rms-item-detail-modal__variant-thumb"
                  />
                  <span class="rms-item-detail-modal__variant-label">{{ variantSkuLabel(sku) }}</span>
                  <span v-if="!isVariantSkuPubliclyOrderable(sku)" class="rms-item-detail-modal__variant-unavail text-xs shrink-0">{{ $t('public.itemNotAvailable') }}</span>
                  <span v-if="sku.price != null" class="rms-item-detail-modal__price" :style="primaryTextStyle">{{ formatPrice(sku.price) }}</span>
                </li>
              </ul>
            </template>
            <template v-else>
              <span v-if="simpleLinePrice != null" class="rms-item-detail-modal__price" :style="primaryTextStyle">{{ formatPrice(simpleLinePrice) }}</span>
              <span v-else-if="item.is_available === false" class="rms-item-detail-modal__muted">{{ $t('public.itemNotAvailable') }}</span>
            </template>
          </div>

          <p v-if="item.description" class="rms-item-detail-modal__desc">{{ item.description }}</p>

          <ul v-if="item.type === 'combo' && comboEntries(item).length" class="rms-item-detail-modal__combo-list" :aria-label="$t('public.comboContentsAria')">
            <li v-for="(entry, eIdx) in comboEntries(item)" :key="eIdx">
              {{ entry.name }} × {{ entry.quantity }}{{ entry.modifier_label ? ` (${entry.modifier_label})` : '' }}
            </li>
          </ul>

          <template v-if="publicModifierGroups(item).length">
            <fieldset
              v-for="group in publicModifierGroups(item)"
              :key="group.uuid || group.name"
              class="rms-item-detail-modal__modifiers"
              :aria-label="group.name?.trim() ? group.name : $t('public.modifiersAria')"
            >
              <legend class="rms-item-detail-modal__modifiers-legend">
                {{ group.name?.trim() ? group.name : $t('public.modifiersHeading') }}
              </legend>
              <ul
                class="rms-item-detail-modal__modifiers-list"
                role="list"
                :aria-label="group.name?.trim() ? group.name : $t('public.modifiersAria')"
              >
                <li v-for="mod in (group.options || [])" :key="mod.uuid" class="rms-item-detail-modal__modifier-row">
                  <label class="rms-item-detail-modal__modifier-label">
                    <input
                      v-if="group.selection_type === 'multiple'"
                      type="checkbox"
                      class="rms-item-detail-modal__modifier-check"
                      :checked="isModifierSelected(mod.uuid)"
                      @change="toggleModifier(mod.uuid, $event.target.checked)"
                    />
                    <input
                      v-else
                      type="radio"
                      class="rms-item-detail-modal__modifier-check"
                      :name="`rms-mod-group-${group.uuid || 'default'}`"
                      :checked="isModifierSelected(mod.uuid)"
                      @change="setSingleGroupModifier(group, mod.uuid)"
                    />
                    <span class="rms-item-detail-modal__modifier-name">{{ mod.name || $t('public.modifierUnnamed') }}</span>
                    <span
                      class="rms-item-detail-modal__modifier-extra"
                      :class="{ 'rms-item-detail-modal__modifier-extra--free': modifierPriceIsFree(mod) }"
                      :style="modifierPriceIsFree(mod) ? undefined : primaryTextStyle"
                    >
                      <template v-if="Number(mod.price_delta) > 0">+ {{ formatPrice(mod.price_delta) }}</template>
                      <template v-else>{{ $t('public.modifierNoExtraCost') }}</template>
                    </span>
                  </label>
                </li>
              </ul>
            </fieldset>
          </template>
          </div>
        </div>

        <!-- Pinned below scroll so qty + Add to cart stay visible on short viewports -->
        <p
          v-if="quickOrderAvailable && item && addToCartBlockedHint"
          class="rms-item-detail-modal__cart-hint"
          role="status"
        >
          {{ addToCartBlockedHint }}
        </p>
        <div v-if="quickOrderAvailable && item" class="rms-item-detail-modal__cart-section">
          <div class="rms-item-detail-modal__cart-qty">
            <button
              type="button"
              class="rms-item-detail-modal__cart-qty-btn"
              :style="primaryButtonStyle"
              :disabled="cartQty <= 1"
              :aria-label="$t('public.quickOrder.ariaDecrease')"
              @click="cartQty = Math.max(1, cartQty - 1)"
            >
              <span class="material-icons" aria-hidden="true">remove</span>
            </button>
            <span class="rms-item-detail-modal__cart-qty-value" aria-live="polite">{{ cartQty }}</span>
            <button
              type="button"
              class="rms-item-detail-modal__cart-qty-btn"
              :style="primaryButtonStyle"
              :disabled="cartQty >= 99"
              :aria-label="$t('public.quickOrder.ariaIncrease')"
              @click="cartQty = Math.min(99, cartQty + 1)"
            >
              <span class="material-icons" aria-hidden="true">add</span>
            </button>
          </div>
          <button
            type="button"
            class="rms-item-detail-modal__cart-add-btn"
            :style="primaryButtonStyle"
            :disabled="addToCartDisabled"
            @click="onAddToCart"
          >
            <span class="material-icons rms-item-detail-modal__cart-add-icon" aria-hidden="true">shopping_cart</span>
            {{ isInCart ? $t('public.quickOrder.updateCart') : $t('public.quickOrder.addToCart') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, reactive, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePublicMenuDisplay } from '@/composables/usePublicMenuDisplay'
import { useScrollLock } from '@/composables/useScrollLock'
import { normalizeModifierUuidList } from '@/models/MenuItemModifier.js'
import { areRequiredSingleChoiceModifiersSatisfied } from '@/utils/publicMenuCart'
import { isVariantSkuPubliclyOrderable } from '@/utils/publicVariantSku'

const { t: $t } = useI18n()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  item: { type: Object, default: null },
  /** When opening from a variant click, the selected SKU (for image and price display). */
  selectedSku: { type: Object, default: null },
  categoryName: { type: String, default: 'Menu' },
  primaryColor: { type: String, default: '' },
  primary_color: { type: String, default: '' },
  currency: { type: String, default: 'USD' },
  showCurrency: { type: Boolean, default: true },
  quickOrderAvailable: { type: Boolean, default: false },
  cartItemQuantity: { type: Number, default: 0 },
  /** When true (e.g. Template 5), variant items require selecting a variant in the modal before Add to cart; variant list is clickable. */
  requireVariantSelection: { type: Boolean, default: false },
  /** Business type for placeholder icon when item has no image: 'restaurants' | 'ecommerce' | 'services' | 'general'. */
  businessType: { type: String, default: 'restaurants' },
  /** Selected public modifier UUIDs (v-model); parent resets when closing the modal. */
  selectedModifierUuids: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'add-to-cart', 'update:selectedModifierUuids', 'update:selectedSku'])

const { lock: lockScroll, unlock: unlockScroll } = useScrollLock()

const panelRef = ref(null)
const closeBtnRef = ref(null)

const primary = computed(() => props.primaryColor || props.primary_color || '#2563eb')
const primaryButtonStyle = computed(() => ({ backgroundColor: primary.value, borderColor: primary.value, color: '#fff' }))
const primaryTextStyle = computed(() => ({ color: primary.value }))

const displayProps = reactive({
  menuGroups: [],
  menuItems: [],
  currency: 'USD',
  showCurrency: true,
})
watch(
  () => [props.item, props.currency, props.showCurrency],
  ([item, currency, showCurrency]) => {
    displayProps.menuItems = item ? [item] : []
    displayProps.currency = currency || 'USD'
    displayProps.showCurrency = showCurrency !== false
  },
  { immediate: true }
)

const {
  formatPrice,
  itemPrice,
  variantSkus,
  variantSkuLabel,
  comboEntries,
} = usePublicMenuDisplay(displayProps)

const modalSelectedSku = ref(null)

watch(
  () => props.item?.uuid,
  () => {
    modalSelectedSku.value = null
  }
)

watch(
  () => props.modelValue,
  (open) => {
    if (!open) {
      modalSelectedSku.value = null
    }
  }
)

/** Set when the card image fails to load so we show the placeholder instead. */
const cardImageError = ref(false)
/** Material symbol name for card placeholder when item has no image; varies by business type. */
const cardPlaceholderIcon = computed(() => {
  const t = (props.businessType || 'restaurants').toLowerCase()
  if (t === 'ecommerce') return 'storefront'
  if (t === 'services') return 'build'
  if (t === 'general') return 'category'
  return 'restaurant'
})
const effectiveSelectedSku = computed(() => {
  if (!props.item || props.item.type !== 'with_variants') return props.selectedSku ?? null
  if (props.requireVariantSelection) return modalSelectedSku.value ?? props.selectedSku ?? null
  return props.selectedSku ?? null
})

function publicModifierGroups(item) {
  if (Array.isArray(item?.modifier_groups) && item.modifier_groups.length) {
    return item.modifier_groups.map((g) => ({
      uuid: g.uuid ?? '',
      name: g.name ?? '',
      selection_type: g.selection_type === 'multiple' ? 'multiple' : 'single',
      options: Array.isArray(g.options) ? g.options : [],
    }))
  }
  if (Array.isArray(item?.modifiers) && item.modifiers.length) {
    return [
      {
        uuid: 'legacy',
        name: '',
        selection_type: 'multiple',
        options: item.modifiers,
      },
    ]
  }
  return []
}

function publicFlattenModifierOptions(item) {
  return publicModifierGroups(item).flatMap((g) => g.options ?? [])
}

/** Show “no extra cost” when price_delta is missing, zero, or not a positive number. */
function modifierPriceIsFree(mod) {
  const n = Number(mod?.price_delta)
  return !(Number.isFinite(n) && n > 0)
}

const selectedModifierSorted = computed(() => normalizeModifierUuidList(props.selectedModifierUuids))

const modifiersDeltaTotal = computed(() => {
  const item = props.item
  if (!item) return 0
  const set = new Set(selectedModifierSorted.value)
  return publicFlattenModifierOptions(item).reduce((sum, m) => {
    if (!m?.uuid || !set.has(String(m.uuid))) return sum
    return sum + (Number(m.price_delta) || 0)
  }, 0)
})

const linePriceWithModifiers = computed(() => {
  const sku = effectiveSelectedSku.value
  if (!sku || sku.price == null) return null
  const base = Number(sku.price)
  if (Number.isNaN(base)) return null
  return base + modifiersDeltaTotal.value
})

const simpleLinePrice = computed(() => {
  const p = itemPrice(props.item)
  if (p == null) return null
  return Number(p) + modifiersDeltaTotal.value
})

function isModifierSelected(uuid) {
  return selectedModifierSorted.value.includes(String(uuid))
}

function toggleModifier(uuid, checked) {
  const id = String(uuid)
  const cur = new Set(props.selectedModifierUuids.map(String))
  if (checked) cur.add(id)
  else cur.delete(id)
  emit('update:selectedModifierUuids', [...cur])
}

/** At most one selected UUID per single-selection group. */
function setSingleGroupModifier(group, optionUuid) {
  const optionIds = new Set((group.options ?? []).map((o) => String(o?.uuid ?? '')).filter(Boolean))
  const next = props.selectedModifierUuids.map(String).filter((id) => !optionIds.has(id))
  next.push(String(optionUuid))
  emit('update:selectedModifierUuids', next)
}
const addToCartDisabled = computed(() => {
  if (!props.item) return true
  if (props.item.type === 'with_variants') {
    const sku = effectiveSelectedSku.value
    if (props.requireVariantSelection && !sku) return true
    if (sku && !isVariantSkuPubliclyOrderable(sku)) return true
  }
  if (!areRequiredSingleChoiceModifiersSatisfied(props.item, props.selectedModifierUuids)) return true
  return false
})

/** Shown above the cart bar when Add to cart is disabled (variant or required modifier choice). */
const addToCartBlockedHint = computed(() => {
  if (!props.item || !props.quickOrderAvailable) return ''
  if (props.item.type === 'with_variants' && props.requireVariantSelection && !effectiveSelectedSku.value) {
    return $t('public.selectVariantToAdd')
  }
  if (
    props.item.type === 'with_variants' &&
    effectiveSelectedSku.value &&
    !isVariantSkuPubliclyOrderable(effectiveSelectedSku.value)
  ) {
    return $t('public.itemNotAvailable')
  }
  if (!areRequiredSingleChoiceModifiersSatisfied(props.item, props.selectedModifierUuids)) {
    return $t('public.selectRequiredModifierChoice')
  }
  return ''
})
function selectVariant(sku) {
  if (!isVariantSkuPubliclyOrderable(sku)) return
  modalSelectedSku.value = sku
  if (props.requireVariantSelection) {
    emit('update:selectedSku', sku)
  }
}

const cardImageUrl = computed(() => {
  const item = props.item
  if (!item) return null
  const sku = effectiveSelectedSku.value
  const skuUrl = sku?.image_url ?? sku?.imageUrl
  if (skuUrl) return resolveImageUrl(skuUrl)
  const itemUrl = item.image_url ?? item.imageUrl
  if (itemUrl) return resolveImageUrl(itemUrl)
  return null
})

/** Resolve relative image URLs (e.g. /api/...) against current origin so images load on public templates. */
function resolveImageUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return null
  const trimmed = url.trim()
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed
  if (typeof window !== 'undefined' && trimmed.startsWith('/')) {
    return window.location.origin + trimmed
  }
  return trimmed
}

const lowestVariantPrice = computed(() => {
  const item = props.item
  if (!item || item.type !== 'with_variants') return null
  const skus = variantSkus(item).filter((s) => isVariantSkuPubliclyOrderable(s))
  if (!skus.length) return null
  const prices = skus.map((s) => (s.price != null ? Number(s.price) : null)).filter((p) => p != null)
  return prices.length ? Math.min(...prices) : null
})

const hasBodyContent = computed(() => {
  const item = props.item
  if (!item) return false
  if (item.description) return true
  if (item.type === 'combo' && comboEntries(item).length) return true
  if (publicModifierGroups(item).length) return true
  if (item.type === 'with_variants') return lowestVariantPrice.value != null || variantSkus(item).length > 0
  return itemPrice(item) != null || item.is_available === false
})

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function getFocusables(container) {
  if (!container) return []
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent != null && !el.hasAttribute('aria-hidden')
  )
}

function onPanelKeydown(e) {
  if (e.key !== 'Tab' || !props.modelValue || !panelRef.value) return
  const focusables = getFocusables(panelRef.value)
  if (focusables.length === 0) return
  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  if (e.shiftKey) {
    if (document.activeElement === first) {
      e.preventDefault()
      last.focus()
    }
  } else {
    if (document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }
}

const cartQty = ref(1)
/** Cart line quantity when the modal opened (from parent; may be 0 if modifier state was reset). */
const lineQtyAtOpen = ref(0)
const isInCart = computed(() => props.cartItemQuantity > 0)

watch(() => props.modelValue, (open) => {
  if (open) {
    cardImageError.value = false
    lineQtyAtOpen.value = Math.max(0, Number(props.cartItemQuantity) || 0)
    cartQty.value = props.cartItemQuantity > 0 ? props.cartItemQuantity : 1
    lockScroll()
    nextTick(() => closeBtnRef.value?.focus())
  } else {
    unlockScroll()
  }
})
watch(cardImageUrl, () => { cardImageError.value = false })

onBeforeUnmount(() => {
  unlockScroll()
})

function onAddToCart() {
  if (addToCartDisabled.value) return
  const openQ = lineQtyAtOpen.value
  const q = cartQty.value
  /**
   * `replace`: cart line gets exactly `q` (absolute). Otherwise we add `q` to the existing line.
   * - Parent may report openQ === 0 after reopen (modifiers reset) while the cart already has this line → replace false merges.
   * - In cart with qty 1, spinner still 1 → merge (+1). With qty ≥ 2, same spinner → replace (idempotent).
   * - User changed spinner vs snapshot → replace (new total).
   */
  const replace = openQ > 0 && (q !== openQ || openQ > 1)
  emit('add-to-cart', {
    item: props.item,
    quantity: q,
    selectedSku: effectiveSelectedSku.value ?? props.selectedSku ?? null,
    modifier_uuids: selectedModifierSorted.value,
    replace,
  })
  close()
}

function close() {
  emit('update:modelValue', false)
}
</script>

<style scoped>
.rms-item-detail-modal {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: max(1rem, env(safe-area-inset-top)) max(1rem, env(safe-area-inset-right)) max(1rem, env(safe-area-inset-bottom)) max(1rem, env(safe-area-inset-left));
  pointer-events: none;
  overflow: hidden;
}

.rms-item-detail-modal[aria-hidden="false"],
.rms-item-detail-modal:not([aria-hidden]) {
  pointer-events: auto;
}

.rms-item-detail-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  pointer-events: auto;
}

.rms-item-detail-modal__panel {
  --rms-detail-accent: v-bind(primary);
  position: relative;
  width: 100%;
  max-width: 420px;
  max-height: min(100dvh, 100%);
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  min-height: 0;
  pointer-events: auto;
  overflow: hidden;
}

@media (max-width: 380px) {
  .rms-item-detail-modal__panel {
    border-radius: 0;
  }
}

.rms-item-detail-modal__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 2;
  min-width: 44px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.rms-item-detail-modal__close:hover {
  opacity: 0.9;
}

.rms-item-detail-modal__close:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.rms-item-detail-modal__close .material-icons {
  font-size: 1.5rem;
}

.rms-item-detail-modal__scroll {
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
  padding: 0;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
}

.rms-item-detail-modal__scroll--body-empty {
  padding-bottom: 0;
}

.rms-item-detail-modal__card {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 1rem 1rem 0 0;
  overflow: hidden;
  flex-shrink: 0;
}

.rms-item-detail-modal__card--no-image {
  aspect-ratio: auto;
  min-height: 0;
  height: 8rem;
  border-radius: 1rem 1rem 0 0;
}

@media (max-width: 380px) {
  .rms-item-detail-modal__card {
    border-radius: 0;
  }
  .rms-item-detail-modal__card--no-image {
    border-radius: 0;
  }
}

.rms-item-detail-modal__card-bg {
  position: absolute;
  inset: 0;
  background: #1e293b;
}

.rms-item-detail-modal__card--no-image .rms-item-detail-modal__card-bg {
  background: linear-gradient(135deg, #334155 0%, #1e293b 50%, #0f172a 100%);
}

.rms-item-detail-modal__card-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.15);
  pointer-events: none;
}

.rms-item-detail-modal__card-placeholder .material-symbols-outlined {
  font-size: 3rem;
}

.rms-item-detail-modal__card-bg--has-image .rms-item-detail-modal__card-img {
  opacity: 1;
}

.rms-item-detail-modal__card-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.2s;
}

.rms-item-detail-modal__card-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.4) 40%, transparent 70%);
  pointer-events: none;
}

.rms-item-detail-modal__card-text {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 1.5rem 1.25rem 1.25rem;
  padding-right: 3.5rem;
}

.rms-item-detail-modal__card--no-image .rms-item-detail-modal__card-text {
  padding: 1rem 1.25rem 1rem;
  padding-right: 3.5rem;
}

.rms-item-detail-modal__card--no-image .rms-item-detail-modal__card-name {
  font-size: 1.75rem;
}

.rms-item-detail-modal__card-category {
  margin: 0 0 0.25rem;
  font-size: 1rem;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.85);
}

.rms-item-detail-modal__card-name {
  margin: 0;
  font-size: 2.2rem;
  font-weight: 600;
  line-height: 1.25;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.rms-item-detail-modal__body {
  padding: 16px 20px 0;
}

@media (min-width: 640px) {
  .rms-item-detail-modal__body {
    padding-left: 20px;
    padding-right: 20px;
  }
}

.rms-item-detail-modal__price-wrap {
  margin-bottom: 0.75rem;
}

.rms-item-detail-modal__price {
  font-size: 1.125rem;
  font-weight: 600;
}

.rms-item-detail-modal__muted {
  font-size: 0.875rem;
  color: #64748b;
}

.rms-item-detail-modal__variant-list {
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0;
}

.rms-item-detail-modal__variant-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-height: 44px;
  padding: 0.25rem 0;
  font-size: 0.875rem;
}

.rms-item-detail-modal__variant-row--unavailable {
  opacity: 0.7;
}

.rms-item-detail-modal__variant-unavail {
  color: #64748b;
}

.rms-item-detail-modal__variant-row--clickable {
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  margin: 0 -0.75rem;
  border-radius: 0.5rem;
  transition: background-color 0.15s ease;
}

.rms-item-detail-modal__variant-row--clickable:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.rms-item-detail-modal__variant-row--clickable.rms-item-detail-modal__variant-row--selected {
  background-color: var(--rms-detail-accent);
  color: #fff;
}

.rms-item-detail-modal__variant-row--clickable.rms-item-detail-modal__variant-row--selected .rms-item-detail-modal__variant-label {
  color: inherit;
}

.rms-item-detail-modal__variant-row--clickable.rms-item-detail-modal__variant-row--selected .rms-item-detail-modal__price {
  color: inherit !important;
}

.rms-item-detail-modal__variant-thumb {
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
  border-radius: 0.375rem;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.rms-item-detail-modal__variant-label {
  color: #334155;
}

.rms-item-detail-modal__desc {
  margin: 0 0 0.75rem;
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.4;
}

.rms-item-detail-modal__combo-list {
  list-style: none;
  padding-left: 1rem;
  margin: 0 0 0 0.125rem;
  border-left: 2px solid #e2e8f0;
  font-size: 0.8125rem;
  color: #64748b;
}

.rms-item-detail-modal__combo-list li {
  margin-top: 0.25rem;
}

.rms-item-detail-modal__modifiers {
  margin: 0 0 1rem;
  padding: 0;
  border: none;
}

.rms-item-detail-modal__modifiers-legend {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #334155;
  padding: 0;
  margin-bottom: 0.5rem;
}

.rms-item-detail-modal__modifiers-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.rms-item-detail-modal__modifier-row {
  margin: 0;
}

.rms-item-detail-modal__modifier-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 44px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #334155;
}

.rms-item-detail-modal__modifier-check {
  width: 1.125rem;
  height: 1.125rem;
  flex-shrink: 0;
}

.rms-item-detail-modal__modifier-name {
  flex: 1;
  text-align: start;
}

.rms-item-detail-modal__modifier-extra {
  font-size: 0.8125rem;
  font-weight: 600;
  flex-shrink: 0;
}

.rms-item-detail-modal__modifier-extra--free {
  font-weight: 500;
  color: #64748b;
}

.rms-item-detail-modal__cart-hint {
  flex-shrink: 0;
  margin: 0;
  padding: 0.5rem 1.25rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #b45309;
  background: #fffbeb;
  border-top: 1px solid #fde68a;
}

.rms-item-detail-modal__cart-section {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  padding-bottom: max(1rem, env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.08);
  position: relative;
  z-index: 1;
}

/* Less vertical hero on short screens so price/modifiers scroll stays usable above the pinned cart */
@media (max-height: 560px) {
  .rms-item-detail-modal__card:not(.rms-item-detail-modal__card--no-image) {
    aspect-ratio: 16 / 9;
    max-height: 38vh;
  }
}

.rms-item-detail-modal__cart-qty {
  display: flex;
  align-items: center;
  gap: 0;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.rms-item-detail-modal__cart-qty-btn {
  min-width: 44px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.15s;
}

.rms-item-detail-modal__cart-qty-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.rms-item-detail-modal__cart-qty-btn:hover:not(:disabled) {
  opacity: 0.85;
}

.rms-item-detail-modal__cart-qty-btn .material-icons {
  font-size: 1.25rem;
}

.rms-item-detail-modal__cart-qty-value {
  min-width: 2.5rem;
  text-align: center;
  font-weight: 600;
  font-size: 1rem;
  color: #0f172a;
  padding: 0 0.25rem;
}

.rms-item-detail-modal__cart-add-btn {
  flex: 1;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: opacity 0.15s;
}

.rms-item-detail-modal__cart-add-btn:hover {
  opacity: 0.9;
}

.rms-item-detail-modal__cart-add-icon {
  font-size: 1.25rem;
}
</style>
